import { DS_HEIGHT, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { Theme, Typography } from '@rhg/libs/material';
import { WarningOutline } from '@rhg/libs/material/icons';
import SimpleDialog from '@rhg/libs/material/simple-dialog';
import { ComponentProps, useEffect, useId } from 'react';

const styleIconContainer = {
    height: DS_HEIGHT.AVATAR_MEDIUM,
    width: DS_HEIGHT.AVATAR_MEDIUM,
    '& > svg': {
        // Need to override default styles in this component
        height: { xs: '24px' },
        width: { xs: '24px' },
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark',
};

const styleTitleContainer = { mb: 2 };
const styleTitle = { mb: 0 };

const styleButton = {
    width: 'initial',
    minWidth: '140px',
    height: '36px',
    m: 0,
};

const styleStackedButton = {
    ...styleButton,
    width: '100%',
    maxWidth: '268px',
};

const styleBottomActionsWrapper = {
    p: DS_SPACING_UNITS.CONTENT,
};

const styleStackedButtons = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};

type DialogPropsSubset = Pick<
    ComponentProps<typeof SimpleDialog>,
    | 'sx'
    | 'icon'
    | 'hideCloseButton'
    | 'iconContainerOverride'
    | 'title'
    | 'trackCloseButton'
    | 'trackDialog'
    | 'trackScreen'
    | 'trackProps'
    | 'trackFeature'
    | 'trackProps'
    | 'content'
    | 'buttons'
>;

const styleContentWrapper = {
    pb: 0,
};

interface RegistrationDialogProps {
    onOpen?: () => void;
    onClose: () => void;
    stackBottomActions?: boolean;
}

export function RegistrationSimpleDialog({
    onOpen,
    onClose,
    sx,
    trackCloseButton,
    trackDialog,
    trackScreen,
    trackFeature,
    trackProps,
    title,
    content,
    buttons,
    icon = <WarningOutline color="warning" />,
    iconContainerOverride,
    stackBottomActions,
    hideCloseButton,
}: DialogPropsSubset & RegistrationDialogProps) {
    const id = useId();

    useEffect(() => {
        onOpen?.();
    }, [onOpen]);

    return (
        <SimpleDialog
            aria-labelledby={id}
            disableMobileFullscreen
            content={
                <>
                    <Typography variant="srOnly" id={id}>
                        {title}
                    </Typography>

                    {content}
                </>
            }
            contentWrapperStyle={styleContentWrapper}
            trackCloseButton={trackCloseButton}
            trackDialog={trackDialog}
            trackScreen={trackScreen}
            trackFeature={trackFeature}
            trackProps={trackProps}
            title={title}
            titleVariant="h6"
            iconContainerOverride={iconContainerOverride}
            iconContainerStyle={styleIconContainer}
            icon={icon}
            fullWidth
            dialogMaxWidth={900}
            onClose={onClose}
            titleStyle={styleTitle}
            titleWrapperStyle={styleTitleContainer}
            isOpen
            transitionDuration={0}
            bottomActionsWrapperStyle={styleBottomActionsWrapper}
            buttonsContainerStyle={
                stackBottomActions ? styleStackedButtons : undefined
            }
            hideCloseButton={hideCloseButton}
            buttons={buttons?.map((button) => ({
                ...button,
                sx: stackBottomActions ? styleStackedButton : styleButton,
            }))}
            sx={sx}
        />
    );
}
